import React, { useState, useEffect } from 'react';
import { X, Save, Edit2, User, Calendar, Tag, DollarSign, Image, FileText, ToggleLeft, ToggleRight } from 'lucide-react';

const ProduitServiceDetail = ({ produit, onClose, onUpdate, onToggleStatus }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    categorie: '',
    is_active: true
  });

  useEffect(() => {
    if (produit) {
      setFormData({
        name: produit.name || '',
        description: produit.description || '',
        price: produit.price || '',
        categorie: produit.categorie || '',
        is_active: produit.is_active
      });
    }
  }, [produit]);

  const handleSubmit = async () => {
    await onUpdate(produit.id, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: produit.name || '',
      description: produit.description || '',
      price: produit.price || '',
      categorie: produit.categorie || '',
      is_active: produit.is_active
    });
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    if (produit.is_active) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Actif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
        Inactif
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-900">Détails du Produit/Service</h2>
            {getStatusBadge()}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'info'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Informations
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Image de couverture */}
              {produit.cover_image_url && (
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Image className="w-4 h-4" />
                    Image de couverture
                  </label>
                  <img
                    src={produit.cover_image_url}
                    alt={produit.name}
                    className="w-full max-h-96 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Nom */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4" />
                  Nom
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 text-lg font-semibold">{produit.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4" />
                  Description
                </label>
                {isEditing ? (
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">{produit.description}</p>
                )}
              </div>

              {/* Prix */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4" />
                  Prix
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                  />
                ) : (
                  <p className="text-2xl font-bold text-[#437011]">
                    {Math.round(produit.price).toLocaleString('fr-FR')} FCFA
                  </p>
                )}
              </div>

              {/* Catégorie */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4" />
                  Catégorie
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{produit.categorie || 'Non spécifiée'}</p>
                )}
              </div>

              {/* Vendeur */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  Vendeur
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900">
                    {produit.user?.first_name} {produit.user?.last_name}
                  </p>
                  <p className="text-sm text-gray-600">@{produit.user?.username}</p>
                  <p className="text-sm text-gray-600">{produit.user?.email}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4" />
                    Date de création
                  </label>
                  <p className="text-gray-900">{formatDate(produit.created_at)}</p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4" />
                    Dernière modification
                  </label>
                  <p className="text-gray-900">{formatDate(produit.updated_at)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-6 py-2 bg-[#437011] text-white rounded-lg hover:bg-[#356009] transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-6 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#E55A00] transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
              )}
            </div>

            <button
              onClick={() => onToggleStatus(produit.id)}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                produit.is_active
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
              }`}
            >
              {produit.is_active ? (
                <>
                  <ToggleLeft className="w-4 h-4" />
                  Désactiver
                </>
              ) : (
                <>
                  <ToggleRight className="w-4 h-4" />
                  Activer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProduitServiceDetail;
